import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { SignupCompleteResponse, VerificationPreview, UserType } from '../../types/auth';
import { LockClosedIcon } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const token = (location.state as any)?.token;
  const preview = (location.state as any)?.preview as VerificationPreview;
  const user_type = (location.state as any)?.user_type as UserType;

  const [username, setUsername] = useState('');
  const [emailLocal, setEmailLocal] = useState(''); // 이메일 아이디 (@ 앞)
  const [emailDomain, setEmailDomain] = useState(''); // 이메일 도메인 (@ 뒤)
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [zipCode, setZipCode] = useState(''); // 우편번호
  const [address, setAddress] = useState(''); // 기본주소
  const [addressDetail, setAddressDetail] = useState(''); // 상세주소
  const [institutionName, setInstitutionName] = useState('');
  const [institutionType, setInstitutionType] = useState('');
  const [businessNumber, setBusinessNumber] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [domainSuggestion, setDomainSuggestion] = useState<string | null>(null);

  // 토큰이 없으면 처음부터 다시 시작
  useEffect(() => {
    if (!token || !preview) {
      navigate('/signup');
    }
  }, [token, preview, navigate]);

  // 이메일 도메인 목록
  const emailDomains = [
    'gmail.com',
    'naver.com',
    'daum.net',
    'kakao.com',
    'hanmail.net',
    'nate.com',
    'hotmail.com',
    'outlook.com',
    'yahoo.com',
    'yahoo.co.kr',
  ];

  // 도메인 선택 핸들러
  const handleDomainSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setEmailDomain(e.target.value);
    setDomainSuggestion(null); // 선택 시 제안 제거
  };

  // Levenshtein distance 계산
  const levenshteinDistance = (str1: string, str2: string): number => {
    const matrix: number[][] = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  };

  // 도메인 입력 시 실시간 오타 감지
  useEffect(() => {
    if (!emailDomain || emailDomain.length < 3) {
      setDomainSuggestion(null);
      return;
    }

    // 도메인이 목록에 이미 있으면 제안 안 함
    if (emailDomains.includes(emailDomain.toLowerCase())) {
      setDomainSuggestion(null);
      return;
    }

    // 가장 유사한 도메인 찾기
    let bestMatch: string | null = null;
    let bestDistance = Infinity;
    const THRESHOLD = 2;

    for (const domain of emailDomains) {
      const distance = levenshteinDistance(emailDomain.toLowerCase(), domain);
      if (distance <= THRESHOLD && distance < bestDistance) {
        bestDistance = distance;
        bestMatch = domain;
      }
    }

    setDomainSuggestion(bestMatch);
  }, [emailDomain]);

  // 제안 적용
  const applyDomainSuggestion = () => {
    if (domainSuggestion) {
      setEmailDomain(domainSuggestion);
      setDomainSuggestion(null);
    }
  };

  // 다음 우편번호 서비스 열기
  const openDaumPostcode = () => {
    new (window as any).daum.Postcode({
      oncomplete: function(data: any) {
        // 우편번호와 주소 정보를 해당 필드에 넣는다
        setZipCode(data.zonecode);

        // 도로명 주소와 지번 주소 중 사용자가 선택한 것을 사용
        const fullAddress = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
        setAddress(fullAddress);

        // 상세주소 입력 필드로 포커스 이동
        document.getElementById('addressDetail')?.focus();
      }
    }).open();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // 유효성 검사
    if (!username || !emailLocal || !emailDomain || !password || !passwordConfirm) {
      setError('모든 필수 항목을 입력해주세요.');
      return;
    }

    // 이메일 아이디 검증
    if (!/^[a-zA-Z0-9._-]+$/.test(emailLocal)) {
      setError('이메일 아이디는 영문, 숫자, 점, 하이픈, 언더스코어만 사용할 수 있습니다.');
      return;
    }

    // 이메일 도메인 검증
    if (!/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(emailDomain)) {
      setError('올바른 이메일 도메인을 입력해주세요.');
      return;
    }

    // 전체 이메일 조합
    const email = `${emailLocal}@${emailDomain}`;

    if (password !== passwordConfirm) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    if (password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      return;
    }

    if (!zipCode || !address) {
      setError('우편번호 검색을 통해 주소를 입력해주세요.');
      return;
    }

    if (user_type === 'institution' && !institutionName) {
      setError('기관명을 입력해주세요.');
      return;
    }

    setLoading(true);

    try {
      // Edge Function 호출: signup-complete
      // 모든 회원가입 로직은 서버에서 처리
      const { data, error: fnError } = await supabase.functions.invoke<SignupCompleteResponse>(
        'signup-complete',
        {
          body: {
            token,
            username,
            email,
            password,
            user_type,
            address: `(${zipCode}) ${address}`, // 우편번호 포함하여 저장
            address_detail: addressDetail,
            institution_name: institutionName,
            institution_type: institutionType,
            business_registration_number: businessNumber,
          }
        }
      );

      // 디버깅: 전체 응답 출력
      console.log('Edge Function response data:', data);
      console.log('Edge Function response error:', fnError);

      if (fnError) {
        console.error('Edge Function error:', fnError);
        // FunctionsHttpError에서 실제 응답 body 확인
        const errorBody = (fnError as any).context?.body;
        console.log('Error response body:', errorBody);

        // ReadableStream인 경우 읽기 시도
        if (errorBody instanceof ReadableStream) {
          try {
            const reader = errorBody.getReader();
            const { value } = await reader.read();
            const text = new TextDecoder().decode(value);
            console.log('Error body text:', text);
            const parsed = JSON.parse(text);
            console.log('Parsed error:', parsed);
            if (parsed?.message) {
              setError(parsed.message + (parsed.debug ? `\n상세: ${parsed.debug}` : ''));
              return;
            }
          } catch (e) {
            console.error('Failed to parse error body:', e);
          }
        } else if (errorBody?.message) {
          setError(errorBody.message);
          return;
        }
        throw fnError;
      }

      if (!data?.success) {
        setError(data?.message || '회원가입에 실패했습니다.');
        return;
      }

      // 성공 시 세션 설정 및 완료 페이지로 이동
      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        });
      }

      navigate('/signup/complete', {
        state: {
          session: data.session,
          user: data.user,
        }
      });

    } catch (err: any) {
      console.error('Signup error:', err);

      // 에러 메시지 파싱
      let errorMessage = '회원가입 중 오류가 발생했습니다.';

      if (err.message?.includes('Failed to send a request')) {
        errorMessage = 'Edge Function에 연결할 수 없습니다. 서버를 확인해주세요.';
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token || !preview) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 bg-gray-50 py-12 mt-20">
        <div className="max-w-5xl mx-auto px-4">
        {/* Contents Head */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h3 className="text-3xl font-bold text-gray-900">통합회원가입</h3>
            <div className="flex gap-4 text-sm">
              <Link to="/login" className="text-gray-600 hover:text-[#48c9a6]">로그인</Link>
              <Link to="/signup" className="text-[#48c9a6] font-medium">통합회원가입</Link>
            </div>
          </div>
          <div className="h-1 bg-[#48c9a6] w-20 mt-4"></div>
        </div>

        {/* Contents Body */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          {/* 회원정보 입력 타이틀 */}
          <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
            <div className="w-10 h-10 bg-[#48c9a6]/10 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-[#48c9a6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </div>
            <span className="text-xl font-bold text-gray-900">회원정보 입력</span>
          </div>

          {/* 본인인증 정보 (읽기 전용) */}
          <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <LockClosedIcon className="h-4 w-4 mr-2" />
              본인인증 정보 (변경 불가)
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">이름:</span>
                <span className="ml-2 font-medium">{preview.name}</span>
              </div>
              <div>
                <span className="text-gray-500">생년월일:</span>
                <span className="ml-2 font-medium">{preview.birthdate}</span>
              </div>
              <div>
                <span className="text-gray-500">성별:</span>
                <span className="ml-2 font-medium">
                  {preview.gender === 'male' ? '남성' : '여성'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">휴대폰:</span>
                <span className="ml-2 font-medium">{preview.phone}</span>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 rounded-lg bg-red-50 border border-red-200 p-4">
              <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
            </div>
          )}

          {/* 가입 폼 */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                아이디 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                placeholder="영문, 숫자, 언더스코어 사용 가능"
                required
              />
              <p className="mt-1 text-xs text-gray-500">로그인 시 사용할 아이디입니다</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                이메일 <span className="text-red-500">*</span>
              </label>
              <div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    id="emailLocal"
                    value={emailLocal}
                    onChange={(e) => setEmailLocal(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="이메일 아이디"
                    maxLength={50}
                    required
                  />
                  <span className="text-gray-500">@</span>
                  <input
                    type="text"
                    id="emailDomain"
                    value={emailDomain}
                    onChange={(e) => setEmailDomain(e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="도메인"
                    maxLength={50}
                    required
                  />
                  <select
                    onChange={handleDomainSelect}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6] bg-white"
                    title="이메일 도메인 선택"
                  >
                    <option value="">직접입력</option>
                    {emailDomains.map((domain) => (
                      <option key={domain} value={domain}>
                        {domain}
                      </option>
                    ))}
                  </select>
                </div>
                {domainSuggestion && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                    <p className="text-sm text-gray-700">
                      혹시 <span className="font-semibold text-gray-900">{domainSuggestion}</span>을 의미하셨나요?
                    </p>
                    <button
                      type="button"
                      onClick={applyDomainSuggestion}
                      className="ml-4 text-sm text-[#48c9a6] hover:text-[#3ab89a] font-medium whitespace-nowrap"
                    >
                      수정
                    </button>
                  </div>
                )}
                <p className="mt-1 text-xs text-gray-500">영수증, 알림 수신용 이메일입니다</p>
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                비밀번호 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                placeholder="최소 6자 이상"
                required
              />
            </div>

            <div>
              <label htmlFor="passwordConfirm" className="block text-sm font-medium text-gray-700">
                비밀번호 확인 <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                id="passwordConfirm"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                placeholder="비밀번호를 다시 입력하세요"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                주소 <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  id="zipCode"
                  value={zipCode}
                  readOnly
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700"
                  placeholder="우편번호"
                />
                <button
                  type="button"
                  onClick={openDaumPostcode}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-md border border-gray-300 transition-colors"
                >
                  우편번호 검색
                </button>
              </div>
              <input
                type="text"
                id="address"
                value={address}
                readOnly
                className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700 mb-2"
                placeholder="기본주소 (우편번호 검색 후 자동입력)"
                required
              />
              <input
                type="text"
                id="addressDetail"
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                placeholder="상세주소 (동/호수 등)"
              />
              <p className="mt-1 text-xs text-gray-500">우편번호 검색 버튼을 클릭하여 주소를 입력해주세요</p>
            </div>

            {/* 기관 전용 필드 */}
            {user_type === 'institution' && (
              <>
                <div>
                  <label htmlFor="institutionName" className="block text-sm font-medium text-gray-700">
                    기관명 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="institutionName"
                    value={institutionName}
                    onChange={(e) => setInstitutionName(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="기관명을 입력하세요"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="institutionType" className="block text-sm font-medium text-gray-700">
                    기관 유형
                  </label>
                  <input
                    type="text"
                    id="institutionType"
                    value={institutionType}
                    onChange={(e) => setInstitutionType(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="예: 학교, 복지관 등"
                  />
                </div>

                <div>
                  <label htmlFor="businessNumber" className="block text-sm font-medium text-gray-700">
                    사업자등록번호
                  </label>
                  <input
                    type="text"
                    id="businessNumber"
                    value={businessNumber}
                    onChange={(e) => setBusinessNumber(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#48c9a6] focus:border-[#48c9a6]"
                    placeholder="000-00-00000"
                  />
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-base font-medium rounded-lg text-white bg-[#48c9a6] hover:bg-[#3ab89a] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#48c9a6] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? '가입 처리 중...' : '가입 완료'}
            </button>
          </form>

          {/* 이전 단계 버튼 */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <button
              onClick={() => navigate('/signup/terms', { state: { token, preview, user_type } })}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              ← 이전 단계로 돌아가기
            </button>
          </div>
        </div>
      </div>
      </div>

      <Footer />
    </div>
  );
};

export default SignupForm;
